import * as THREE from '../../libs/three.js/build/three.module.js';
import { Measure } from './Measure.js';
import { Utils } from '../utils.js';
import { CameraMode } from '../defines.js';
import { EventDispatcher } from '../EventDispatcher.js';

function updateAzimuth(viewer, measure) {
  const azimuth = measure.azimuth;

  const isOkay = measure.points.length === 2;

  azimuth.node.visible = isOkay && measure.showAzimuth;

  if (!azimuth.node.visible) {
    return;
  }

  const camera = viewer.scene.getActiveCamera();
  const renderAreaSize = viewer.renderer.getSize(new THREE.Vector2());
  const width = renderAreaSize.width;
  const height = renderAreaSize.height;

  const [p0, p1] = measure.points;
  const r = p0.position.distanceTo(p1.position);
  const northVec = Utils.getNorthVec(p0.position, r, viewer.getProjection());
  const northPos = p0.position.clone().add(northVec);

  azimuth.center.position.copy(p0.position);
  azimuth.center.scale.set(2, 2, 2);

  azimuth.center.visible = false;
  // azimuth.target.visible = false;

  {
    // north
    azimuth.north.position.copy(northPos);
    azimuth.north.scale.set(2, 2, 2);

    let distance = azimuth.north.position.distanceTo(camera.position);
    let pr = Utils.projectedRadius(1, camera, distance, width, height);

    let scale = 5 / pr;
    azimuth.north.scale.set(scale, scale, scale);
  }

  {
    // target
    azimuth.target.position.copy(p1.position);
    azimuth.target.position.z = azimuth.north.position.z;

    let distance = azimuth.target.position.distanceTo(camera.position);
    let pr = Utils.projectedRadius(1, camera, distance, width, height);

    let scale = 5 / pr;
    azimuth.target.scale.set(scale, scale, scale);
  }

  azimuth.circle.position.copy(p0.position);
  azimuth.circle.scale.set(r, r, r);
  azimuth.circle.material.resolution.set(width, height);

  // to target
  azimuth.centerToTarget.geometry.setPositions([0, 0, 0, ...p1.position.clone().sub(p0.position).toArray()]);
  azimuth.centerToTarget.position.copy(p0.position);
  azimuth.centerToTarget.geometry.verticesNeedUpdate = true;
  azimuth.centerToTarget.geometry.computeBoundingSphere();
  azimuth.centerToTarget.computeLineDistances();
  azimuth.centerToTarget.material.resolution.set(width, height);

  // to target ground
  azimuth.centerToTargetground.geometry.setPositions([0, 0, 0, p1.position.x - p0.position.x, p1.position.y - p0.position.y, 0]);
  azimuth.centerToTargetground.position.copy(p0.position);
  azimuth.centerToTargetground.geometry.verticesNeedUpdate = true;
  azimuth.centerToTargetground.geometry.computeBoundingSphere();
  azimuth.centerToTargetground.computeLineDistances();
  azimuth.centerToTargetground.material.resolution.set(width, height);

  // to north
  azimuth.centerToNorth.geometry.setPositions([0, 0, 0, northPos.x - p0.position.x, northPos.y - p0.position.y, 0]);
  azimuth.centerToNorth.position.copy(p0.position);
  azimuth.centerToNorth.geometry.verticesNeedUpdate = true;
  azimuth.centerToNorth.geometry.computeBoundingSphere();
  azimuth.centerToNorth.computeLineDistances();
  azimuth.centerToNorth.material.resolution.set(width, height);

  // label
  const radians = Utils.computeAzimuth(p0.position, p1.position, viewer.getProjection());
  let degrees = THREE.MathUtils.radToDeg(radians); // Исправлено: THREE.MathUtils вместо THREE.Math
  if (degrees < 0) {
    degrees = 360 + degrees;
  }
  const txtDegrees = `${degrees.toFixed(2)}°`;
  const labelDir = northPos.clone().add(p1.position).multiplyScalar(0.5).sub(p0.position);
  if (labelDir.length() > 0) {
    labelDir.z = 0;
    labelDir.normalize();
    const labelVec = labelDir.clone().multiplyScalar(r);
    const labelPos = p0.position.clone().add(labelVec);
    azimuth.label.position.copy(labelPos);
  }
  azimuth.label.setText(txtDegrees);
  let distance = azimuth.label.position.distanceTo(camera.position);
  let pr = Utils.projectedRadius(1, camera, distance, width, height);
  let scale = 70 / pr;
  azimuth.label.scale.set(scale, scale, scale);
}

export class MeasuringTool extends EventDispatcher {
  constructor(viewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.activeMeasure = null;
    this.isInInsertionMode = false;
    
    // Mode system: CREATE or MOVE
    this.currentMode = 'CREATE'; // 'CREATE' or 'MOVE'
    this.modeCallbacks = new Map(); // Store callbacks for mode switching

    this.volumeLabel = document.createElement('div');
    this.volumeLabel.className = 'volume-label';
    this.volumeLabel.style.position = 'absolute';
    this.volumeLabel.style.backgroundColor = 'rgba(0,0,0,0.8)';
    this.volumeLabel.style.color = 'white';
    this.volumeLabel.style.padding = '4px 8px';
    this.volumeLabel.style.borderRadius = '3px';
    this.volumeLabel.style.fontSize = '12px';
    this.volumeLabel.style.fontWeight = 'normal';
    this.volumeLabel.style.zIndex = '10000';
    this.volumeLabel.style.border = '2px solid #00ff00';
    this.volumeLabel.style.display = 'none';
    this.volumeLabel.innerHTML = 'Volume: 0';
    document.body.appendChild(this.volumeLabel);

    this.addEventListener('start_inserting_measurement', e => {
      this.viewer.dispatchEvent({
        type: 'cancel_insertions',
      });
    });

    this.showLabels = true;
    this.scene = new THREE.Scene();
    this.scene.name = 'scene_measurement';
    this.light = new THREE.PointLight(0xffffff, 1.0);
    this.scene.add(this.light);

    this.viewer.inputHandler.registerInteractiveScene(this.scene);

    this.onRemove = e => {
      this.scene.remove(e.measurement);
    };
    this.onAdd = e => {
      this.scene.add(e.measurement);
      this.setupSphereInteractions(e.measurement);
    };

    for (let measurement of viewer.scene.measurements) {
      this.onAdd({ measurement: measurement });
    }

    viewer.addEventListener('update', this.update.bind(this));
    viewer.addEventListener('render.pass.perspective_overlay', this.render.bind(this));
    viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

    viewer.scene.addEventListener('measurement_added', this.onAdd);
    viewer.scene.addEventListener('measurement_removed', this.onRemove);

    // Add keyboard event listener for undo functionality
    this.keyboardHandler = this.handleKeyboard.bind(this);
    document.addEventListener('keydown', this.keyboardHandler);
  }

  // Mode switching methods
  setMode(mode) {
    if (mode !== 'CREATE' && mode !== 'MOVE') {
      console.warn('Invalid mode. Use CREATE or MOVE');
      return;
    }
    
    const oldMode = this.currentMode;
    this.currentMode = mode;
    
    // Update cursor for the new mode
    this.updateCursorForMode();
    
    // Update existing measurements to respect the new mode
    const measurements = this.viewer.scene.measurements;
    for (let measurement of measurements) {
      this.enableSphereMovement(measurement);
    }
    
    // Don't dispatch events that might cancel insertion
    // Instead just execute callbacks directly if needed
    if (this.modeCallbacks.has(mode)) {
      this.modeCallbacks.get(mode)();
    }
    
    console.log(`Measuring tool mode changed to: ${mode} (insertion mode: ${this.isInInsertionMode})`);
  }

  getMode() {
    return this.currentMode;
  }

  onModeChange(mode, callback) {
    this.modeCallbacks.set(mode, callback);
  }

  isCreateMode() {
    return this.currentMode === 'CREATE';
  }

  isMoveMode() {
    return this.currentMode === 'MOVE';
  }

  highlightLatestPoint(measure) {
    if (!measure.spheres || measure.spheres.length === 0) return;
    
    const latestIndex = measure.spheres.length - 1;
    
    // EXPLICITLY set ALL spheres: older ones to white, latest to red
    measure.spheres.forEach((sphere, index) => {
      if (index === latestIndex) {
        // This is the latest point - make it red and protected
        sphere.userData.isLatest = true;
        sphere.material.color.setHex(0xff0000);
      } else {
        // This is an older point - make it white and unprotected
        sphere.userData.isLatest = false;
        sphere.material.color.setHex(0xffffff);
      }
    });
  }

  setupSphereInteractions(measure) {
    if (!measure.spheres) return;

    measure.spheres.forEach((sphere, index) => {
      if (sphere.userData.clickHandler) {
        sphere.removeEventListener('click', sphere.userData.clickHandler);
      }
      
      // Colors will be updated by updateSphereColors method

      const clickHandler = (event) => {
        if (!this.isInInsertionMode) return;

        // Only allow point creation in CREATE mode
        if (this.isCreateMode()) {
          const newPosition = measure.points[index].position.clone();
          measure.addMarker(newPosition);

          const newSphereIndex = measure.spheres.length - 1;
          this.viewer.inputHandler.startDragging(measure.spheres[newSphereIndex]);

          this.setupSphereInteractions(measure);
        }
        
        return false; // Prevent further event handling
      };

      sphere.userData.clickHandler = clickHandler;
      sphere.addEventListener('click', clickHandler);

      const mouseEnterHandler = () => {
        if (this.isInInsertionMode) {
          // Different colors for different modes
          if (this.isCreateMode()) {
            sphere.material.color.setHex(0x00ff00); // Green for CREATE mode
          } else if (this.isMoveMode()) {
            sphere.material.color.setHex(0x0066ff); // Blue for MOVE mode
          }
        }
      };

      const mouseLeaveHandler = () => {
        // Restore proper color: red for latest, white for others
        if (sphere.userData.isLatest) {
          sphere.material.color.setHex(0xff0000); // Keep latest red
        } else {
          sphere.material.color.setHex(0xffffff); // Others to white
        }
      };

      sphere.addEventListener('mouseenter', mouseEnterHandler);
      sphere.addEventListener('mouseleave', mouseLeaveHandler);
    });
    
    // Enable sphere movement based on current mode
    this.enableSphereMovement(measure);
  }

  onSceneChange(e) {
    if (e.oldScene) {
      e.oldScene.removeEventListener('measurement_added', this.onAdd);
      e.oldScene.removeEventListener('measurement_removed', this.onRemove);
    }

    e.scene.addEventListener('measurement_added', this.onAdd);
    e.scene.addEventListener('measurement_removed', this.onRemove);
  }

  onModeChanged(mode) {
    console.log('MeasuringTool: Mode changed to', mode);
    
    // If we're in insertion mode and have an active measure, update the sphere interactions
    if (this.isInInsertionMode && this.activeMeasure) {
      console.log('MeasuringTool: Updating sphere interactions for mode', mode);
      this.setupSphereInteractions(this.activeMeasure);
      
      // Reset the last click time to ensure new clicks work
      this.lastClickTime = 0;
      console.log('MeasuringTool: Reset click timer for new mode');
      
      // If switching to move mode, stop any current dragging to allow point selection
      if (mode === 'move' && this.viewer.inputHandler.drag) {
        console.log('MeasuringTool: Stopping current drag for move mode');
        this.viewer.inputHandler.drag = null;
      }
    }
  }

  updateSphereColors(measure) {
    if (!measure.spheres || measure.spheres.length === 0) return;
    
    console.log('Updating sphere colors, total spheres:', measure.spheres.length);
    
    // Set all spheres to white except the last one
    measure.spheres.forEach((sphere, index) => {
      if (sphere.material) {
        if (index === measure.spheres.length - 1) {
          // Last sphere: red
          sphere.material.color.setHex(0xff0000);
          console.log(`Sphere ${index}: Set to RED`);
        } else {
          // Other spheres: white
          sphere.material.color.setHex(0xffffff);
          console.log(`Sphere ${index}: Set to WHITE`);
        }
        sphere.material.needsUpdate = true;
      }
    });
  }

  trackPositionChange(measure, sphereIndex, oldPosition, newPosition) {
    // Store the position change for undo
    this.positionHistory.push({
      measure: measure,
      sphereIndex: sphereIndex,
      oldPosition: oldPosition.clone(),
      newPosition: newPosition.clone(),
      timestamp: Date.now()
    });
    
    console.log('Tracked position change:', {
      sphereIndex,
      oldPosition: oldPosition.toArray(),
      newPosition: newPosition.toArray()
    });
  }

  getNormalizedDrag() {
    // Get drag distance from the input handler
    if (this.viewer.inputHandler && this.viewer.inputHandler.drag) {
      const drag = this.viewer.inputHandler.drag;
      const diff = new THREE.Vector2().subVectors(drag.end, drag.start);
      const domElement = this.viewer.renderer.domElement;
      diff.x = diff.x / domElement.clientWidth;
      diff.y = diff.y / domElement.clientHeight;
      return diff;
    }
    return new THREE.Vector2(0, 0);
  }

  startInsertion(args = {}) {
    let domElement = this.viewer.renderer.domElement;

    let measure = new Measure();

    measure.closed = true;
    measure.showArea = false;
    measure.showHeight = false;

    this.dispatchEvent({
      type: 'start_inserting_measurement',
      measure: { ...measure, name: args.name },
    });

    const pick = (defaul, alternative) => {
      if (defaul != null) {
        return defaul;
      } else {
        return alternative;
      }
    };

    measure.showDistances = args.showDistances === null ? true : args.showDistances;

    measure.showArea = pick(args.showArea, false);
    measure.showAngles = pick(args.showAngles, false);
    measure.showCoordinates = pick(args.showCoordinates, false);
    measure.showHeight = pick(args.showHeight, false);
    measure.showCircle = pick(args.showCircle, false);
    measure.showAzimuth = pick(args.showAzimuth, false);
    measure.showEdges = pick(args.showEdges, true);
    measure.closed = pick(args.closed, false);
    measure.maxMarkers = pick(args.maxMarkers, Infinity);

    measure.name = args.name || 'Measurement';

    this.scene.add(measure);
    this.activeMeasure = measure;
    this.isInInsertionMode = true;
    
    // Update cursor for insertion mode
    this.updateCursorForMode();

    let cancel = {
      removeLastMarker: measure.maxMarkers > 3,
      callback: null,
    };

    let insertionCallback = e => {
    // Only process actual mouseup events, not mousemove or other events
    if (e.type !== 'mouseup') {
      return;
    }
    
    // Prevent multiple executions of the same click
    const currentTime = Date.now();
    if (currentTime - this.lastClickTime < 100) {
      console.log('Skipping duplicate click');
      return;
    }
    this.lastClickTime = currentTime;
    
    // Check if this is a drag operation (camera movement) vs intentional click
    const dragDistance = this.getNormalizedDrag();
    if (dragDistance.length() > 0.003) { // Reduced threshold for more sensitive drag detection
      console.log('Skipping click - detected drag operation, distance:', dragDistance.length());
      return;
    }
    
    console.log('=== CLICK PROCESSING ===');
    console.log('Time since last click:', currentTime - this.lastClickTime);
    console.log('Is in insertion mode:', this.isInInsertionMode);
    console.log('Active measure:', !!this.activeMeasure);
    console.log('Drag distance:', dragDistance.length());
    
    if (e.button === THREE.MOUSE.LEFT) {
      if (!this.isInInsertionMode) {
        this.isInInsertionMode = true;
        this.activeMeasure = measure;
      }

      // Check the current point mode from the context menu
      const currentMode = this.viewer.inputHandler.getCurrentPointMode();
      const intersectedSphere = this.getIntersectedSphere(e, measure);

      if (intersectedSphere) {
        const sphereIndex = measure.spheres.indexOf(intersectedSphere);
        if (sphereIndex !== -1) {
          // In CREATE mode, create new points but don't drag
          if (this.isCreateMode()) {
            const newPosition = measure.points[sphereIndex].position.clone();
            measure.addMarker(newPosition);
            this.setupSphereInteractions(measure);
            this.highlightLatestPoint(measure);
            // Don't start dragging in CREATE mode
          } else if (this.isMoveMode()) {
            // In MOVE mode, start dragging existing point
            this.viewer.inputHandler.startDragging(intersectedSphere);
          }
          return;
        }
      }

        // Only create new points in CREATE mode
        if (this.isCreateMode()) {
          measure.addMarker(measure.points[measure.points.length - 1].position.clone());
          this.setupSphereInteractions(measure);
          this.highlightLatestPoint(measure);
          // Don't start dragging in CREATE mode
        }
    } else if (e.button === THREE.MOUSE.RIGHT) {
      // Don't cancel on right-click, let the context menu handle it
      console.log('Right-click detected, not canceling measurement');
      // The context menu will be shown by the InputHandler
    }
};

    cancel.callback = e => {
      if (cancel.removeLastMarker) {
        measure.removeMarker(measure.points.length - 1);
      }
      domElement.removeEventListener('mouseup', insertionCallback, true);
      this.viewer.removeEventListener('cancel_insertions', cancel.callback);
      this.activeMeasure = null;
      this.isInInsertionMode = false;
      
      // Reset cursor when exiting insertion mode
      this.updateCursorForMode();
      
      // Dispatch event to notify sidebar to clear active tool button
      console.log('MeasuringTool: Dispatching cancel_insertions event');
      this.viewer.dispatchEvent({
        type: 'cancel_insertions',
        source: 'MeasuringTool'
      });
    };

    if (measure.maxMarkers > 1) {
      this.viewer.addEventListener('cancel_insertions', cancel.callback);
      domElement.addEventListener('mouseup', insertionCallback, true);
    }

    measure.addMarker(new THREE.Vector3(0, 0, 0));
    this.viewer.inputHandler.startDragging(measure.spheres[measure.spheres.length - 1]);

    this.setupSphereInteractions(measure);
    this.highlightLatestPoint(measure);

    this.viewer.scene.addMeasurement(measure);

    return measure;
  }

  getIntersectedSphere(event, measure) {
    const rect = this.viewer.renderer.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2();

    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    const camera = this.viewer.scene.getActiveCamera();

    raycaster.setFromCamera(mouse, camera);

    const intersects = raycaster.intersectObjects(measure.spheres);

    if (intersects.length > 0) {
      return intersects[0].object;
    }

    return null;
  }

  update() {
    let camera = this.viewer.scene.getActiveCamera();
    let domElement = this.renderer.domElement;
    let measurements = this.viewer.scene.measurements;

    const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
    let clientWidth = renderAreaSize.width;
    let clientHeight = renderAreaSize.height;

    this.light.position.copy(camera.position);

    let volumeMeasure = null;
    for (let measure of measurements) {
      if (measure.points.length >= 3) {
        volumeMeasure = measure;
        this.updateFilledArea(measure);
        break;
      }
    }

    if (volumeMeasure) {
      try {
        const volume = this.computeVolume(volumeMeasure);

        const center = new THREE.Vector3();
        volumeMeasure.points.forEach(p => center.add(p.position));
        center.divideScalar(volumeMeasure.points.length);

        const pos = center.clone().project(camera);
        const w = this.renderer.domElement.clientWidth;
        const h = this.renderer.domElement.clientHeight;

        if (pos.x >= -1 && pos.x <= 1 && pos.y >= -1 && pos.y <= 1 && pos.z >= -1 && pos.z <= 1) {
          this.volumeLabel.style.left = `${(pos.x + 1) * 0.5 * w}px`;
          this.volumeLabel.style.top = `${(-pos.y + 1) * 0.5 * h}px`;
          this.volumeLabel.innerHTML = `Volume: ${volume.toFixed(2)} m³`;
          this.volumeLabel.style.display = 'block';
        } else {
          this.volumeLabel.style.display = 'none';
        }
      } catch (error) {
        console.warn('Volume calculation error:', error);
        this.volumeLabel.style.display = 'none';
      }
    } else {
      this.volumeLabel.style.display = 'none';
    }

    for (let measure of measurements) {
      measure.lengthUnit = this.viewer.lengthUnit;
      measure.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
      measure.update();

      updateAzimuth(this.viewer, measure);

      // spheres
      for (let sphere of measure.spheres) {
        let distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));
        let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
        let scale = 15 / pr;
        sphere.scale.set(scale, scale, scale);
      }

      // labels
      let labels = measure.edgeLabels.concat(measure.angleLabels);
      for (let label of labels) {
        let distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
        let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
        let scale = 70 / pr;

        if (typeof Potree !== 'undefined' && Potree.debug && Potree.debug.scale) {
          scale = Potree.debug.scale / pr;
        }

        label.scale.set(scale, scale, scale);
      }

      // coordinate labels
      for (let j = 0; j < measure.coordinateLabels.length; j++) {
        let label = measure.coordinateLabels[j];
        let sphere = measure.spheres[j];

        let distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));

        let screenPos = sphere.getWorldPosition(new THREE.Vector3()).clone().project(camera);
        screenPos.x = Math.round(((screenPos.x + 1) * clientWidth) / 2);
        screenPos.y = Math.round(((-screenPos.y + 1) * clientHeight) / 2);
        screenPos.z = 0;
        screenPos.y -= 30;

        let labelPos = new THREE.Vector3((screenPos.x / clientWidth) * 2 - 1, -(screenPos.y / clientHeight) * 2 + 1, 0.5);
        labelPos.unproject(camera);
        if (this.viewer.scene.cameraMode == CameraMode.PERSPECTIVE) {
          let direction = labelPos.sub(camera.position).normalize();
          labelPos = new THREE.Vector3().addVectors(camera.position, direction.multiplyScalar(distance));
        }
        label.position.copy(labelPos);
        let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      // height label
      if (measure.showHeight) {
        let label = measure.heightLabel;

        {
          let distance = label.position.distanceTo(camera.position);
          let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
          let scale = 70 / pr;
          label.scale.set(scale, scale, scale);
        }

        {
          // height edge
          let edge = measure.heightEdge;

          let sorted = measure.points.slice().sort((a, b) => a.position.z - b.position.z);
          let lowPoint = sorted[0].position.clone();
          let highPoint = sorted[sorted.length - 1].position.clone();
          let min = lowPoint.z;
          let max = highPoint.z;

          let start = new THREE.Vector3(highPoint.x, highPoint.y, min);
          let end = new THREE.Vector3(highPoint.x, highPoint.y, max);

          let lowScreen = lowPoint.clone().project(camera);
          let startScreen = start.clone().project(camera);
          let endScreen = end.clone().project(camera);

          let toPixelCoordinates = v => {
            let r = v.clone().addScalar(1).divideScalar(2);
            r.x = r.x * clientWidth;
            r.y = r.y * clientHeight;
            r.z = 0;

            return r;
          };

          let lowEL = toPixelCoordinates(lowScreen);
          let startEL = toPixelCoordinates(startScreen);
          let endEL = toPixelCoordinates(endScreen);

          let lToS = lowEL.distanceTo(startEL);
          let sToE = startEL.distanceTo(endEL);

          edge.geometry.lineDistances = [0, lToS, lToS, lToS + sToE];
          edge.geometry.lineDistancesNeedUpdate = true;

          edge.material.dashSize = 10;
          edge.material.gapSize = 10;
        }
      }

      {
        // area label
        let label = measure.areaLabel;
        let distance = label.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      {
        // radius label
        let label = measure.circleRadiusLabel;
        let distance = label.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

        let scale = 70 / pr;
        label.scale.set(scale, scale, scale);
      }

      {
        // edges
        const materials = [
          measure.circleRadiusLine.material,
          ...measure.edges.map(e => e.material),
          measure.heightEdge.material,
          measure.circleLine.material,
        ];

        for (const material of materials) {
          material.resolution.set(clientWidth, clientHeight);
        }
      }

      if (!this.showLabels) {
        const labels = [
          ...measure.sphereLabels,
          ...measure.edgeLabels,
          ...measure.angleLabels,
          ...measure.coordinateLabels,
          measure.heightLabel,
          measure.areaLabel,
          measure.circleRadiusLabel,
        ];

        for (const label of labels) {
          label.visible = false;
        }
      }
    }
  }

  render() {
    this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
  }

  computeVolume(measure) {
    const points = measure.points.map(p => p.position.clone());
    
    if (points.length < 3) {
      return 0;
    }

    try {
        // Simple triangulation of the polygon footprint
        const triangles = this.triangulatePolygon(points);
        
        let totalVolume = 0;
        
        // Find the minimum Z (reference level)
        const zRef = Math.min(...points.map(p => p.z));
        
        for (let tri of triangles) {
            const [p1, p2, p3] = tri;
            
            // Calculate triangle area in XY plane
            const Axy = 0.5 * Math.abs(
                (p2.x - p1.x) * (p3.y - p1.y) - (p3.x - p1.x) * (p2.y - p1.y)
            );
            
            // Calculate average height above reference
            const hAvg = (p1.z + p2.z + p3.z) / 3 - zRef;
            
            // Only add positive volumes (above reference level)
            if (hAvg > 0) {
                const triangleVolume = Axy * hAvg;
                totalVolume += triangleVolume;
            }
        }
        
        return totalVolume;
        
    } catch (error) {
        console.error('Volume calculation error:', error);
        return 0;
    }
  }

  // Simple ear clipping triangulation for convex polygons
  triangulatePolygon(points) {
    if (points.length < 3) return [];
    if (points.length === 3) return [points];
    
    const triangles = [];
    const indices = points.map((_, i) => i);
    
    // For convex polygons, we can use a simple fan triangulation
    // Connect each vertex to the first vertex
    for (let i = 1; i < points.length - 1; i++) {
        triangles.push([points[0], points[i], points[i + 1]]);
    }
    
    return triangles;
  }

  handleKeyboard(event) {
    // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.undoLastAction();
    }
  }

  undoLastAction() {
    // First check if we have position changes to undo
    if (this.positionHistory.length > 0) {
      const lastChange = this.positionHistory.pop();
      console.log('Undoing position change:', lastChange);
      
      // Restore the old position
      const sphere = lastChange.measure.spheres[lastChange.sphereIndex];
      if (sphere) {
        sphere.position.copy(lastChange.oldPosition);
        lastChange.measure.points[lastChange.sphereIndex].position.copy(lastChange.oldPosition);
        console.log('Restored position to:', lastChange.oldPosition.toArray());
      }
      return;
    }
    
    // If no position changes, handle point creation/removal
    let targetMeasurement = null;
    const measurements = this.viewer.scene.measurements;
    console.log('All measurements:', measurements);  
    if (this.isInInsertionMode && this.activeMeasure) {
      // If we're actively working on a measurement, undo that one
      targetMeasurement = this.activeMeasure;
      console.log('Undoing active measurement with', targetMeasurement.points.length, 'points');
    } else {
      console.log(this.viewer.scene);
      
      // Otherwise, find the most recent measurement
      const measurements = this.viewer.scene.measurements;
      console.log('All measurements:', measurements);
      
      if (measurements.length === 0) {
        console.log('No measurements to undo');
        return;
      }

      // Get the most recently added measurement
      targetMeasurement = measurements[measurements.length - 1];
      console.log('Undoing most recent measurement with', targetMeasurement, 'points');
    }
    
    if (!targetMeasurement || targetMeasurement.points.length === 0) {
      console.log('No points to undo');
      return;
    }

    if (targetMeasurement.points.length === 1) {
      // If only one point, remove the entire measurement
      this.viewer.scene.removeMeasurement(targetMeasurement);
      console.log('Removed entire measurement');
      // Clear active measurement if it was removed
      if (this.activeMeasure === targetMeasurement) {
        this.activeMeasure = null;
        this.isInInsertionMode = false;
        
        // Reset cursor when exiting insertion mode
        this.updateCursorForMode();
        
        // Dispatch event to notify sidebar to clear active tool button
        console.log('MeasuringTool: Dispatching cancel_insertions event (measurement removed via undo)');
        this.viewer.dispatchEvent({
          type: 'cancel_insertions',
          source: 'MeasuringTool_undo'
        });
      }
    } else {
      // If multiple points, remove the last marker
      console.log({targetMeasurementPoints: targetMeasurement.points});
      const isClassified = targetMeasurement.points[0].classification;
      console.log({isClassified});
      
      targetMeasurement.removeMarker(isClassified ? 0 : targetMeasurement.points.length - 1);
      console.log('Removed last marker, now has', targetMeasurement.points.length, 'points');
    }
  }

  // Enhanced sphere interaction setup with mode awareness
  enableSphereMovement(measure) {
    if (!measure.spheres) return;
    
    measure.spheres.forEach((sphere) => {
      // Remove any existing drag handlers
      if (sphere.userData.dragHandler) {
        sphere.removeEventListener('drag', sphere.userData.dragHandler);
      }
      
      const dragHandler = (event) => {
        // Only allow dragging in MOVE mode
        if (!this.isMoveMode()) {
          return false; // Prevent dragging in CREATE mode
        }
        return true; // Allow dragging in MOVE mode
      };
      
      sphere.userData.dragHandler = dragHandler;
      sphere.addEventListener('drag', dragHandler);
    });
  }

  updateCursorForMode() {
    const domElement = this.viewer.renderer.domElement;
    
    if (this.isInInsertionMode) {
      if (this.isCreateMode()) {
        domElement.style.cursor = 'crosshair';
      } else if (this.isMoveMode()) {
        domElement.style.cursor = 'move';
      }
    } else {
      domElement.style.cursor = 'default';
    }
  }

  dispose() {
    if (this.volumeLabel && this.volumeLabel.parentNode) {
      this.volumeLabel.parentNode.removeChild(this.volumeLabel);
    }
    
    // Remove keyboard event listener
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
    
    // Reset cursor
    this.viewer.renderer.domElement.style.cursor = 'default';
  }

  createFilledArea(measure) {
    if (measure.points.length < 3) return;

    const points = measure.points.map(p => p.position.clone());
    
    // Create a simple triangulated mesh
    const triangles = this.triangulatePolygon(points);
    
    const vertices = [];
    const indices = [];
    
    // Add vertices
    points.forEach(point => {
      vertices.push(point.x, point.y, point.z);
    });
    
    // Add triangle indices
    triangles.forEach(triangle => {
      const i1 = points.indexOf(triangle[0]);
      const i2 = points.indexOf(triangle[1]);
      const i3 = points.indexOf(triangle[2]);
      indices.push(i1, i2, i3);
    });
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    measure.filledMesh = mesh;
    this.scene.add(mesh);
  }

  updateFilledArea(measure) {
    if (!measure.filledMesh) return;
    this.scene.remove(measure.filledMesh);
    this.createFilledArea(measure);
  }
}